import { tt } from '@agos/shared'

/**
 * The Control Tower → manager handoff, which §14.5 asks be specified before it is ported.
 *
 * The Tower's *Otvoriť manažér* button opens `https://<manager>/auth-token?t=<jwt>`. Legacy's whole page is:
 *
 * ```ts
 * const token = params.get("t");
 * if (token) { localStorage.setItem("fk_token", token); window.location.replace("/"); }
 * else navigate("/login");
 * ```
 *
 * Three things about that are worth stating rather than copying.
 *
 * **FB-179 — the previous person stays on the screen.** `AuthProvider` renders from the cached `fk_user`
 * immediately (*"Render from the cached user immediately; `/auth/me` corrects it"*), and the handoff writes
 * only `fk_token`. So after a jump the shell paints the **previous** account's name, role and menu while
 * carrying the new token — and the sidebar is built from `systemRole`. A jump into an account with narrower
 * access shows the wider menu until `/auth/me` answers, and every item on it that the new token cannot
 * reach then 403s. The cached user and the cached permissions are cleared here, so the shell renders from
 * nothing and fills in from the server.
 *
 * **The token is not looked at.** Anything in `?t=` is stored. A malformed value is stored just as happily
 * as a JWT, and the app then boots into a 401 loop with no way back except clearing storage by hand. The
 * shape is checked — three dot-separated base64url segments — before anything is written. This is not
 * verification: the signature is the server's business and nothing here can check it. It is the difference
 * between a bad token and a truncated URL.
 *
 * **The redirect ignores the base path.** `window.location.replace("/")` is absolute, while the same
 * codebase's logout uses `import.meta.env.BASE_URL + "login"`. Under a sub-path deployment the handoff
 * would leave the app. The base is honoured here.
 *
 * The hard reload is **kept**, and legacy's comment is the reason: *"Hard reload so the AuthProvider
 * re-reads the token. wouter's navigate alone keeps the old AuthProvider state."* A client-side navigation
 * would leave the provider holding the old token.
 */

export type HandoffOutcome =
  | { kind: 'accepted'; token: string }
  | { kind: 'missing' }
  | { kind: 'malformed'; value: string }

/**
 * A JWT's shape: three base64url segments separated by dots.
 *
 * Deliberately only the shape. Decoding the payload here would invite a check on its contents, and the one
 * that matters — the signature — cannot be done in a browser. The server is the authority; this is a guard
 * against a truncated or mangled URL, not an authentication step.
 */
const JWT_SHAPE = /^[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+$/

export function readHandoff(search: string): HandoffOutcome {
  const token = new URLSearchParams(search).get('t')
  if (token === null || token.trim() === '') return { kind: 'missing' }
  const trimmed = token.trim()
  if (!JWT_SHAPE.test(trimmed)) return { kind: 'malformed', value: trimmed }
  return { kind: 'accepted', token: trimmed }
}

export function handoffMessage(outcome: HandoffOutcome): string {
  if (outcome.kind === 'accepted') return tt('Prihlasujem do manažéra…')
  if (outcome.kind === 'missing') {
    return tt('Táto adresa neniesla prihlasovací token. Prihláste sa, prosím, normálne.')
  }
  return tt('Prihlasovací token v adrese je poškodený - pravdepodobne sa odkaz skrátil. Skúste ho otvoriť z Control Tower znova.')
}

/** Where to send somebody when the handoff cannot proceed. */
export function fallbackPath(base: string): string {
  return `${withSlash(base)}login`
}

/** Where a successful handoff lands. `/` is the dashboard, as legacy has it. */
export function landingPath(base: string): string {
  return withSlash(base)
}

function withSlash(base: string): string {
  if (base === '') return '/'
  return base.endsWith('/') ? base : `${base}/`
}
